import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  DownloadOutlined,
  ArticleOutlined,
  PrivacyTipOutlined,
  SecurityOutlined,
  GavelOutlined,
} from '@mui/icons-material';
import { useConfig } from '../utils/useConfig';
import Seo from '../components/seo/Seo';

const S3_PDF_URL =
  'https://eltimex-dev.s3.eu-central-1.amazonaws.com/terms%26conditions/Regulamin+sklepu+internetowego+ELTIMEX+(1).pdf';

const TermsAndConditions: React.FC = () => {
  const config = useConfig();

  const [isLoading, setIsLoading] = useState(true);

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Nagłówek strony */}
        <div className="mb-10">
          <motion.h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Regulamin i Polityka Prywatności
          </motion.h1>
          <motion.p
            className="text-gray-600 text-lg max-w-3xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Zapoznaj się z zasadami korzystania z naszego sklepu oraz sposobem
            przetwarzania danych osobowych.
          </motion.p>
        </div>

        {/* Sekcja z kartami informacyjnymi */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col items-center text-center">
            <div className="bg-primary/10 p-3 rounded-full mb-4">
              <GavelOutlined className="text-primary text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Regulamin sklepu
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Ogólne warunki sprzedaży, zasady realizacji zamówień oraz
              reklamacje.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col items-center text-center">
            <div className="bg-primary/10 p-3 rounded-full mb-4">
              <PrivacyTipOutlined className="text-primary text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Polityka prywatności
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Informacje o przetwarzaniu danych osobowych i wykorzystaniu plików
              cookies.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col items-center text-center">
            <div className="bg-primary/10 p-3 rounded-full mb-4">
              <SecurityOutlined className="text-primary text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Bezpieczeństwo danych
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Stosujemy najwyższe standardy bezpieczeństwa przy przetwarzaniu
              Twoich danych.
            </p>
          </div>
        </motion.div>

        {/* Kontener z dokumentem */}
        <motion.div
          className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="p-4 sm:p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <ArticleOutlined className="text-gray-500" />
              <h2 className="text-lg font-medium text-gray-900">
                Regulamin sklepu internetowego {config.company.name}
              </h2>
            </div>

            <motion.a
              href={S3_PDF_URL}
              download
              className="flex items-center justify-center gap-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors text-sm font-medium"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <DownloadOutlined fontSize="small" />
              <span>Pobierz dokument</span>
            </motion.a>
          </div>

          <div className="relative">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              </div>
            )}

            <iframe
              src={S3_PDF_URL}
              className="w-full h-[70vh]"
              title="Regulamin i Polityka Prywatności"
              onLoad={() => setIsLoading(false)}
            />
          </div>
        </motion.div>

        {/* Dodatkowe informacje */}
        <motion.div
          className="mt-8 text-center text-gray-500 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <p>Ostatnia aktualizacja: 01.01.2023</p>
          <p className="mt-2">
            W przypadku pytań dotyczących regulaminu lub polityki prywatności,
            prosimy o kontakt z naszym działem obsługi klienta.
          </p>
        </motion.div>
      </div>
      <Seo
        title={`Regulamin, Polityka Prywatności`}
        description="Regulamin i Polityka Prywatności firmy"
      ></Seo>
    </>
  );
};

export default TermsAndConditions;
